import { useEffect, useMemo, useRef, useState } from 'react';
import { CSSTransition } from 'react-transition-group';
import { playSound, Sound } from '~/assets/sounds';
import { useZoomFactor } from '~/hooks';
import { useShake } from '../../hooks';
import { AppConnectionHelpColors } from '../AppConnectionHelpColors';
import { AppConnectionSwitch } from '../AppConnectionSwitch';
import { ErrorOverlay } from '../ErrorOverlay';
import {
	generateGameField,
	indicatorColors,
} from './AppConnectionSwitchGameField.model';
import { StyledContainer } from './AppConnectionSwitchGameField.styled';

interface SwitchState {
	id: number;
	x: number;
	y: number;
	isVertical: boolean;
	isActive: boolean;
	colors: {
		inactive: string;
		active: string;
	};
	pairId?: number;
}

const AppConnectionSwitchGameField = ({
	handleGameSolved,
}: {
	handleGameSolved: () => void;
}) => {
	const {
		bg,
		switches: initialSwitches,
		level,
		colors,
		mapNumber,
	} = useMemo(() => generateGameField(), []);
	const [switches, setSwitches] = useState<SwitchState[]>(() =>
		initialSwitches.map((switch_) => {
			if (level === 1) {
				return {
					...switch_,
					colors: {
						inactive: indicatorColors.default[0],
						active: indicatorColors.default[1],
					},
				};
			} else {
				const pairIndex = Math.floor(initialSwitches.indexOf(switch_) / 2);
				const pairColor = colors[pairIndex];
				return {
					...switch_,
					colors: {
						inactive: pairColor,
						active: indicatorColors.default[1],
					},
					pairId: pairIndex,
				};
			}
		}),
	);
	const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
	const [isFlashlightActive, setIsFlashlightActive] = useState(false);
	const [isComplete, setIsComplete] = useState(false);
	const [selectedPair, setSelectedPair] = useState<number | null>(null);
	const [currentColorIndex, setCurrentColorIndex] = useState(0);
	const [activatedSwitchesCount, setActivatedSwitchesCount] = useState(0);
	const [errorCount, setErrorCount] = useState(0);
	const containerRef = useRef<HTMLDivElement>(null);
	const zoomFactor = useZoomFactor();
	const [flashlightOpacity, setFlashlightOpacity] = useState(1);
	const [showErrorScreen, setShowErrorScreen] = useState(false);
	const { shake } = useShake();

	useEffect(() => {
		const handleMouseMove = (e: MouseEvent) => {
			if (!containerRef.current || isComplete) return;

			const rect = containerRef.current.getBoundingClientRect();
			const x = (e.clientX - rect.left) / zoomFactor;
			const y = (e.clientY - rect.top) / zoomFactor;

			const isInside =
				x >= 0 &&
				x <= rect.width / zoomFactor &&
				y >= 0 &&
				y <= rect.height / zoomFactor;

			if (isInside !== isFlashlightActive) {
				setIsFlashlightActive(isInside);
			}

			if (isInside) {
				setMousePosition({ x, y });
			}
		};

		window.addEventListener('mousemove', handleMouseMove);
		return () => window.removeEventListener('mousemove', handleMouseMove);
	}, [isFlashlightActive, zoomFactor, isComplete]);

	useEffect(() => {
		if (isComplete) {
			const fadeOut = () => {
				setFlashlightOpacity((prev) => {
					if (prev <= 0) return 0;
					return prev - 0.05;
				});
			};

			const interval = setInterval(fadeOut, 50);
			return () => clearInterval(interval);
		}
	}, [isComplete]);

	useEffect(() => {
		if (isComplete) {
			handleGameSolved();
		}
	}, [isComplete, handleGameSolved]);

	const checkLevelCompletion = (
		updatedSwitches: SwitchState[],
		currentCount: number,
	) => {
		if (level === 1) {
			return updatedSwitches.every((switch_) => switch_.isActive);
		} else if (level === 2) {
			const pairs = new Map<number, boolean>();
			updatedSwitches.forEach((switch_) => {
				if (switch_.pairId !== undefined) {
					const pairSwitches = updatedSwitches.filter(
						(s) => s.pairId === switch_.pairId,
					);
					const isPairComplete = pairSwitches.every((s) => s.isActive);
					pairs.set(switch_.pairId, isPairComplete);
				}
			});
			return Array.from(pairs.values()).every((isActive) => isActive);
		} else if (level === 3) {
			return currentCount === switches.length;
		}
		return false;
	};

	const resetSwitches = () => {
		setSwitches((prevSwitches) =>
			prevSwitches.map((switch_) => ({
				...switch_,
				isActive: false,
			})),
		);
		setSelectedPair(null);
		setCurrentColorIndex(0);
		setActivatedSwitchesCount(0);
	};

	const showError = () => {
		setShowErrorScreen(true);
		shake();
		playSound(Sound.ElectricLoss);
		setTimeout(() => {
			setShowErrorScreen(false);
		}, 3000);
	};

	const handleSwitch = (switchId: number) => {
		if (isComplete) return;

		setSwitches((prevSwitches) => {
			const updatedSwitches = prevSwitches.map((switch_) => ({ ...switch_ }));
			const clickedSwitch = updatedSwitches.find(
				(switch_) => switch_.id === switchId,
			);
			if (!clickedSwitch) return prevSwitches;

			let newActivatedCount = activatedSwitchesCount;

			if (level === 1) {
				clickedSwitch.isActive = !clickedSwitch.isActive;
			} else if (level === 2 && clickedSwitch.pairId !== undefined) {
				if (clickedSwitch.isActive) {
					return prevSwitches;
				}

				if (selectedPair === null) {
					setSelectedPair(clickedSwitch.pairId);
					clickedSwitch.isActive = true;
				} else if (selectedPair === clickedSwitch.pairId) {
					clickedSwitch.isActive = true;
					setSelectedPair(null);
				} else {
					const currentSequenceSwitches = updatedSwitches.filter(
						(s) =>
							s.pairId === selectedPair || s.pairId === clickedSwitch.pairId,
					);

					currentSequenceSwitches.forEach((switch_) => {
						switch_.isActive = false;
					});

					setSelectedPair(clickedSwitch.pairId);
					clickedSwitch.isActive = true;

					const newErrorCount = errorCount + 1;
					setErrorCount(newErrorCount);
					if (newErrorCount >= 1) {
						showError();
					}
				}
			} else if (level === 3 && clickedSwitch.pairId !== undefined) {
				if (clickedSwitch.isActive) {
					return prevSwitches;
				}

				const currentColor = colors[currentColorIndex];
				const switchColor = clickedSwitch.colors.inactive;

				if (selectedPair === null) {
					if (switchColor === currentColor) {
						setSelectedPair(clickedSwitch.pairId);
						clickedSwitch.isActive = true;
						newActivatedCount += 1;
						setActivatedSwitchesCount(newActivatedCount);
					} else {
						resetSwitches();
						const newErrorCount = errorCount + 1;
						setErrorCount(newErrorCount);
						if (newErrorCount >= 1) {
							showError();
						}
						return prevSwitches;
					}
				} else if (selectedPair === clickedSwitch.pairId) {
					clickedSwitch.isActive = true;
					setSelectedPair(null);
					setCurrentColorIndex((prev) => prev + 1);
					newActivatedCount += 1;
					setActivatedSwitchesCount(newActivatedCount);
				} else {
					resetSwitches();
					const newErrorCount = errorCount + 1;
					setErrorCount(newErrorCount);
					if (newErrorCount >= 1) {
						showError();
					}
					return prevSwitches;
				}
			}

			const completed = checkLevelCompletion(
				updatedSwitches,
				newActivatedCount,
			);
			setIsComplete(completed);

			return updatedSwitches;
		});
	};

	const flashlightStyle: React.CSSProperties = {
		background: `radial-gradient(
            ellipse 200px 125px at ${mousePosition.x}px ${mousePosition.y}px,
            ${isFlashlightActive ? 'transparent' : 'rgba(0, 0, 0, 1)'} 0%,
            rgba(0, 0, 0, 0.95) 90%,
            rgba(0, 0, 0, 1) 100%
        )`,
		position: 'absolute',
		top: 0,
		left: 0,
		right: 0,
		bottom: 0,
		pointerEvents: 'none',
		transition: 'background 0.3s ease-in-out',
		opacity: flashlightOpacity,
	};

	return (
		<StyledContainer
			ref={containerRef}
			style={{
				backgroundImage: `url(${bg})`,
			}}
		>
			{switches.map((item) => (
				<AppConnectionSwitch
					key={item.id}
					x={item.x}
					y={item.y}
					isVertical={item.isVertical}
					isActive={item.isActive}
					colors={item.colors}
					handleSwitch={() => handleSwitch(item.id)}
				/>
			))}
			{level === 3 && (
				<AppConnectionHelpColors colors={colors} mapNumber={mapNumber} />
			)}
			<div style={flashlightStyle} />
			<CSSTransition
				in={showErrorScreen}
				classNames="overlay"
				timeout={300}
				unmountOnExit
			>
				<ErrorOverlay />
			</CSSTransition>
		</StyledContainer>
	);
};

export { AppConnectionSwitchGameField };
